import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import {
  DEFAULT_BOM,
  DEFAULT_ROBOT,
  ROBOTS,
  applyEdits,
  buildSkeleton,
  checkManual,
  defaultState,
  defaultTask,
  defaultTrim,
  presetFor,
  pruneEdits,
  type EditKey,
  type Edits,
  type Envelope,
  type FeatureKey,
  type GaitKind,
  type GaitTask,
  type GaitTrim,
  type HumanoidModel,
  type BomParams,
  type ManualCheck,
  type ManualPose,
  type OffState,
  type Robot,
  type Side,
  type Simulation,
  type SimState,
} from '@/domain/hs1'
import { applyPlm, type PlmAssembly } from '@/domain/plm'
import { orbitRelease, orbitStart, type OrbitState } from '@/components/stage/orbit'
import { DEFAULT_OVERLAYS, type OverlayFlags } from '@/components/stage/Overlays'
import type { StageView } from '@/components/stage/framing'
import { createRunCache } from './runCache'

/**
 * Der Zustand des Simulators — an EINER Stelle, für alle Seiten.
 *
 * Warum ein Kontext und kein Zustand je Seite: die Seiten zeigen verschiedene
 * Sichten auf DASSELBE Ergebnis. Eine Kadenz, die auf der Parameterseite anders
 * steht als auf der Antriebsseite, wäre nicht nur unpraktisch — sie würde zwei
 * verschiedene Roboter nebeneinanderstellen, ohne dass es jemand merkt.
 *
 * WAS HIER NICHT HINEINGEHÖRT: die aktuelle Phase im Zyklus, die Kameraposition,
 * ausgewählte Diagrammspuren, das Farbschema. Das ist Bedienzustand. Er darf
 * gespeichert werden, aber getrennt — er verändert kein Rechenergebnis, und
 * sobald sich beides mischt, ist nicht mehr erkennbar, was eine Zahl beeinflusst.
 */

/**
 * Was die Bühne von der Umhüllung braucht: die Körper und die Entscheidung,
 * welche davon wie gezeigt werden.
 */
export interface EnvelopeView {
  envelope: Envelope
  selected: ReadonlySet<string>
  ghosts: boolean
  hulls: boolean
}

interface SimulationContextValue {
  robot: Robot
  /** Das Grundmodell des Roboters, OHNE Änderungen — die Vergleichsbasis. */
  base: HumanoidModel
  /** Das gerechnete Modell: Grundmodell plus Änderungen. */
  model: HumanoidModel
  /** Die Abweichungen vom Grundmodell, einzeln. */
  edits: Edits
  state: SimState
  simulation: Simulation
  /**
   * Rechnet einen ANDEREN Zustand mit demselben Modell durch, ohne ihn zu setzen.
   *
   * Für Vergleiche: die Wirkungsübersicht legt fünfzehn Modellstufen einzeln um
   * und braucht dafür fünfzehn Ergebnisse, von denen keines der angezeigte
   * Zustand ist. Ohne diesen Weg müsste eine Seite den Kontext kurz verstellen
   * und wieder zurückstellen — und jede andere Seite sähe das Flackern mit.
   */
  simulate: (state: SimState) => Simulation
  /**
   * Eine von Hand gestellte Pose — oder `null`, wenn die Figur den Gang läuft.
   *
   * Sie steht hier und nicht in `SimState`, weil sie KEIN Rechenergebnis
   * verändert: Gang, Kinetik und Antriebe bleiben, was sie sind. Sie sagt nur,
   * was die Bühne zeigt. Beides zu mischen hiesse, eine Grenzlagenprüfung
   * stillschweigend zur Betriebsbedingung zu machen.
   */
  /**
   * BEDIENZUSTAND DER BÜHNE — Ansicht und Überlagerungen.
   *
   * Er steht hier und NICHT in `SimState`, und der Grund steht schon oben:
   * Kameraposition und sichtbare Ebenen verändern kein Rechenergebnis. Sobald
   * sich beides mischt, ist nicht mehr erkennbar, was eine Zahl beeinflusst.
   *
   * Er steht trotzdem im Kontext und nicht in der Seite, weil die Bühne über
   * allen Routen hängt: eine Seite, die ihn lokal führte, verlöre ihn beim
   * Wechsel — und die Bühne, die er steuert, bliebe stehen.
   */
  view: StageView
  setView: (view: StageView) => void
  overlays: OverlayFlags
  setOverlay: (key: keyof OverlayFlags, on: boolean) => void

  /*
    DIE BÜHNENSTEUERUNG.

    Sie steht hier und nicht in `SimState`, weil sie NICHTS RECHNET: eine andere
    Kamera, ein angehaltener Zyklus oder ein halbes Tempo ändern kein Ergebnis.
    Läge sie im Zustand, löste jedes Drehen am Rad einen vollen Durchlauf des
    Kerns aus — und die Seiten müssten sich fragen, ob sie neu zeichnen.
  */
  /** Läuft der Zyklus? */
  playing: boolean
  setPlaying: (on: boolean) => void
  /** Zeitlupe: 1 = Echtzeit, 0.25 = Viertel. */
  speed: number
  setSpeed: (value: number) => void
  /**
   * Die Stelle im Zyklus, 0 … 1.
   *
   * Eine REFERENZ und kein Zustand: sie ändert sich sechzigmal je Sekunde, und
   * React hätte damit sechzigmal je Sekunde zu tun. Wer sie anzeigt, liest sie
   * im eigenen Bildtakt; wer sie setzt, schreibt hinein.
   */
  phase: MutableRefObject<number>
  /** Die Kamera in der Hand des Betrachters. */
  orbit: MutableRefObject<OrbitState>
  /** Ob sie gerade übernommen ist — daran hängt der Knopf „Zentrieren". */
  cameraTaken: boolean
  setCameraTaken: (taken: boolean) => void
  /** Gibt die Kamera an die Bühne zurück. */
  releaseCamera: () => void
  /**
   * Ist gerade ein Bühnenfenster offen?
   *
   * Daran hängt eine Sparmassnahme: die Hintergrundbühne hält an, solange ein
   * Messfenster läuft. Zwei WebGL-Kontexte gleichzeitig zu zeichnen kostet
   * spürbar, und einer davon ist in dem Moment Dekoration hinter einer Karte.
   */
  viewerOpen: boolean
  setViewerOpen: (open: boolean) => void
  /**
   * Ist das Bühnenfenster aufgeklappt?
   *
   * NICHT DASSELBE WIE `viewerOpen`. Das meldet die Komponente selbst, damit
   * die Hintergrundbühne pausiert; dieses hier ist die ENTSCHEIDUNG des
   * Benutzers und gilt über alle Seiten. Wer es einmal zuklappt, findet es auf
   * der nächsten Seite zugeklappt vor.
   */
  panelOpen: boolean
  setPanelOpen: (open: boolean) => void

  /**
   * Die Annahmen der Stücklistenrechnung.
   *
   * WARUM SIE HIER STEHEN UND NICHT AUF DER SEITE. Sie standen in der
   * Stückliste, weil dort gerechnet wird. Als Eingabe gehören sie trotzdem zu
   * den Parametern: Momentendichte, Sicherheitsfaktor und Energiedichte sind
   * Annahmen über die Technik, genau wie Kadenz und Körpermasse Annahmen über
   * die Aufgabe sind. Wer sie sucht, sucht sie bei den Eingaben.
   *
   * WARUM SIE NICHT IN `SimState` LIEGEN. Der Rechenkern kennt sie nicht — die
   * Stückliste rechnet hinter ihm. Läge die Momentendichte im Zustand, liefe
   * bei jedem Zug am Regler ein vollständiger Durchlauf des Kerns für ein
   * Ergebnis, das sich davon nicht ändert.
   */
  bom: BomParams
  setBom: (patch: Partial<BomParams>) => void
  /**
   * Von Hand gesetzte Einzelmassen der Stückliste, je Positionsschlüssel.
   *
   * Sie ziehen mit um, weil sie sonst beim Seitenwechsel verloren gingen —
   * und eine Handeingabe, die ein Klick auf eine andere Kachel löscht, ist
   * keine Eingabe, sondern ein Ärgernis.
   */
  bomOverrides: Record<string, number>
  setBomOverride: (key: string, value: number | null) => void
  /** Nimmt alle Handeingaben zurück — die Stückliste rechnet wieder von selbst. */
  resetBomOverrides: () => void

  /**
   * Die angeklickte Achse — was im Bühnenfenster markiert wird.
   *
   * WOFÜR SIE DA IST. Eine Antriebstabelle nennt eine Achse „Knie sagittal
   * rechts" und daneben 306 Nm. Wer die Maschine nicht im Kopf hat, weiss
   * damit, WIE VIEL, aber nicht WO. Ein Klick auf die Zeile setzt einen Ring
   * an genau dieses Gelenk, und die Frage ist beantwortet, ohne dass jemand
   * eine Zeichnung daneben legen muss.
   *
   * SIE IST ANZEIGE UND KEIN ERGEBNIS — wie Kamera, Überlagerungen und
   * Transport. Eine markierte Achse ändert keine Zahl; läge sie im Zustand,
   * löste jeder Klick auf eine Tabellenzeile einen vollen Durchlauf des
   * Rechenkerns aus.
   */
  focus: AxisFocus | null
  setFocus: (focus: AxisFocus | null) => void

  manual: ManualPose | null
  /**
   * Dieselbe Pose, auf den Boden gestellt und auf Freigang geprüft — oder
   * `null`. Die Bühne bekommt sie fertig; sie soll nicht rechnen.
   */
  manualCheck: ManualCheck | null

  /**
   * Die Umhüllung, die das Bühnenfenster zeichnen soll — oder `null`.
   *
   * SIE STEHT HIER UND NICHT AUF DER SEITE, weil sie im Bühnenfenster landet
   * und das auf der Seite nur eingehängt ist. Sie ist ANZEIGE und kein
   * Ergebnis: ein eingeschaltetes Hüllvolumen ändert keine Zahl.
   *
   * Gerechnet wird sie von der Seite Freigang und fertig hereingereicht — die
   * Bühne soll nicht rechnen, und niemand sonst soll die Rechnung bezahlen.
   */
  envelope: EnvelopeView | null
  setEnvelope: (view: EnvelopeView | null) => void
  setManual: (pose: ManualPose | null) => void
  setRobot: (id: string) => void
  setState: (patch: Partial<SimState>) => void
  setTrim: (patch: Partial<GaitTrim>) => void
  setTask: (patch: Partial<GaitTask>) => void
  setGait: (kind: GaitKind) => void
  /**
   * Rechnet eine Gangart durch, ohne sie zu schalten.
   *
   * ZWEI VERWENDUNGEN, UND BEIDE HAENGEN AM SELBEN PUNKT. Die Akkuauslegung
   * braucht den Durchlauf zum GEHEN, gleichgueltig, welche Gangart gerade
   * laeuft. Das Spiel braucht ihn im Voraus, damit der Wechsel mitten im Lauf
   * kein Bild kostet.
   *
   * Der Unterschied zu `simulate({ ...state, gait })` ist der Grund, warum es
   * diesen Weg gibt: dort blieben `trim` und `task` die der LAUFENDEN Gangart,
   * der Zustand waere nach jedem Wechsel ein anderer, und der Zwischenspeicher
   * ginge jedes Mal daneben. Hier ist es derselbe Zustand, den auch `setGait`
   * erzeugt — einmal gerechnet, danach nachgeschlagen.
   */
  gaitRun: (kind: GaitKind) => Simulation
  setPreset: (key: string) => void
  setFeature: (key: FeatureKey, on: boolean) => void
  /**
   * Legt eine Winkelfreiheit still oder gibt sie wieder frei.
   *
   * `null` gibt frei. Das ist eine RECHENGRÖSSE und kein Bedienzustand: eine
   * stillgelegte Achse ändert Gang, Bodenkraft, Antriebsliste und Stückliste.
   */
  setOff: (dof: string, off: OffState | null) => void
  /**
   * Die übernommene PLM-Baugruppe — oder `null`.
   *
   * Sie liegt NEBEN dem Modell wie die Änderungen: der geschätzte Stand bleibt
   * damit jederzeit ablesbar, und „zurücknehmen" ist ein `null` statt einer
   * Wiederherstellung. Was sie an den Massen ändert, steht in `rollup.ts`.
   */
  plm: PlmAssembly | null
  setPlm: (assembly: PlmAssembly | null) => void
  /** Setzt einen Modellwert. `null` nimmt die Änderung zurück. */
  setEdit: (key: EditKey, value: number | null) => void
  /** Nimmt alle Modelländerungen zurück. */
  resetEdits: () => void
  reset: () => void
}

/**
 * Eine Achse, auf die im Bühnenfenster gezeigt wird.
 *
 * Sie trägt KEINE Zahlen. Die Werte stehen in der Tabelle, aus der geklickt
 * wurde; hier steht nur, wohin der Ring gehört und wie er beschriftet wird.
 * Zahlen an zwei Stellen zu führen hiesse, sie irgendwann auseinanderlaufen zu
 * lassen.
 */
export interface AxisFocus {
  /** Gelenk, benannt wie im Rechenkern: `knee`, `hip`, `shoulder`, … */
  joint: string
  /** Seite. `null` für die Mittelkette — Becken, Rumpf, Hals. */
  side: Side | null
  /** Was in der Marke steht. */
  label: string
}

const SimulationContext = createContext<SimulationContextValue | null>(null)

/**
 * Der Zustand, den ein Gangwechsel erzeugt.
 *
 * Steht ausserhalb der Komponente, weil ihn ZWEI Wege brauchen: das Schalten
 * und das Vorausrechnen. Zwei Stellen, die denselben Zustand aufbauen, laufen
 * irgendwann auseinander — und dann rechnet der Zwischenspeicher an der Sache
 * vorbei, ohne dass es jemand sieht.
 */
function gaitState(current: SimState, kind: GaitKind): SimState {
  return {
    ...current,
    gait: kind,
    trim: defaultTrim(kind),
    task: defaultTask(kind),
    preset: defaultState(kind).preset,
  }
}

export function SimulationProvider({ children }: { children: ReactNode }) {
  const [robotId, setRobotId] = useState<string>(DEFAULT_ROBOT)
  const [state, setSimState] = useState<SimState>(() => defaultState('walk'))
  /*
    Die Modelländerungen. Sie liegen NEBEN dem Roboter und nicht in ihm: der
    Grundstand bleibt damit jederzeit ablesbar, jede einzelne Abweichung lässt
    sich zurücknehmen, und ein Roboterwechsel trägt sie mit, soweit die
    Schlüssel passen.
  */
  const [edits, setEdits] = useState<Edits>({})
  const [manual, setManual] = useState<ManualPose | null>(null)
  const [envelope, setEnvelope] = useState<EnvelopeView | null>(null)
  const [view, setView] = useState<StageView>('frei')
  const [overlays, setOverlays] = useState<OverlayFlags>(DEFAULT_OVERLAYS)
  const [playing, setPlaying] = useState(true)
  const [speed, setSpeed] = useState(1)
  const phase = useRef(0)
  const orbit = useRef<OrbitState>(orbitStart())
  const [cameraTaken, setCameraTaken] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [panelOpen, setPanelOpen] = useState(true)
  const [bom, setBomState] = useState<BomParams>(DEFAULT_BOM)
  const [bomOverrides, setBomOverrides] = useState<Record<string, number>>({})
  const [focus, setFocus] = useState<AxisFocus | null>(null)
  const [plm, setPlm] = useState<PlmAssembly | null>(null)

  const releaseCamera = useCallback(() => {
    orbit.current = orbitRelease(orbit.current)
    setCameraTaken(false)
  }, [])

  const setOverlay = useCallback((key: keyof OverlayFlags, on: boolean) => {
    setOverlays((current) => ({ ...current, [key]: on }))
  }, [])

  const setBom = useCallback((patch: Partial<BomParams>) => {
    setBomState((current) => ({ ...current, ...patch }))
  }, [])

  /* `null` nimmt die Handeingabe zurück und stellt den Schätzwert wieder her.
     Ein leeres Feld muss zum Ausgangszustand führen — sonst gibt es kein
     Zurück ausser dem Neuladen. */
  const resetBomOverrides = useCallback(() => setBomOverrides({}), [])

  const setBomOverride = useCallback((key: string, value: number | null) => {
    setBomOverrides((current) => {
      const next = { ...current }
      if (value === null) delete next[key]
      else next[key] = value
      return next
    })
  }, [])

  const robot = ROBOTS[robotId] ?? ROBOTS[DEFAULT_ROBOT]
  const base = robot.model

  /*
    DIE REIHENFOLGE IST EINE AUSSAGE: erst die Handänderungen, dann die
    gemessenen Werte aus dem PLM. Wer eine Masse von Hand gesetzt hat und danach
    das PLM übernimmt, bekommt den gemessenen Wert — eine Messung schlägt eine
    Schätzung, auch die eigene. Andersherum überschriebe eine alte Handeingabe
    stillschweigend eine frische Wägung.
  */
  const model = useMemo(() => applyPlm(applyEdits(base, edits), plm), [base, edits, plm])

  /*
    Der Rechenkern läuft nur, wenn sich Modell oder Zustand ändern. Er ist eine
    reine Funktion, deshalb genügt der Vergleich der Verweise — es gibt keinen
    verborgenen Zustand, der sich dazwischen ändern könnte.

    Dahinter liegt zusätzlich ein kleiner Zwischenspeicher (`runCache.ts`). Der
    Verweisvergleich hier fängt nur den Fall ab, dass sich gar nichts geändert
    hat; der Zwischenspeicher fängt den Fall ab, dass man auf einen Zustand
    ZURÜCKKEHRT — im Spiel bei jedem Gangwechsel, auf den Seiten bei jedem
    Zurückstellen eines Reglers.
  */
  const run = useMemo(createRunCache, [])
  const simulation = useMemo(() => run(model, state), [run, model, state])

  /* Die Handpose wird hier geprüft und nicht in der Bühne: die Bühne zeichnet
     sechzigmal je Sekunde und darf dabei nicht rechnen. Die Prüfung hängt an
     Modell und Pose und läuft nur, wenn sich eines von beiden ändert. */
  const manualCheck = useMemo(
    () => (manual ? checkManual(model, buildSkeleton(model), manual) : null),
    [model, manual],
  )

  const simulate = useCallback((other: SimState) => run(model, other), [run, model])

  const setState = useCallback((patch: Partial<SimState>) => {
    setSimState((current) => ({ ...current, ...patch }))
  }, [])

  const setTrim = useCallback((patch: Partial<GaitTrim>) => {
    setSimState((current) => ({ ...current, trim: { ...current.trim, ...patch } }))
  }, [])

  const setTask = useCallback((patch: Partial<GaitTask>) => {
    setSimState((current) => ({ ...current, task: { ...current.task, ...patch } }))
  }, [])

  /*
    Ein Gangartwechsel setzt Regler UND Aufgabe neu. Die alten Werte
    weiterzuschleppen wäre die unangenehmere Variante: eine Sprintkadenz von 240
    im Gehmodus sieht wie eine Einstellung aus und ist ein Überbleibsel.
  */
  const setGait = useCallback((kind: GaitKind) => {
    setSimState((current) => gaitState(current, kind))
  }, [])

  /* `gaitState` ist hier nicht Bequemlichkeit, sondern die Bedingung: waere der
     vorgerechnete Zustand auch nur in einem Feld ein anderer als der spaeter
     geschaltete, ginge der Zwischenspeicher daneben und die Rechnung liefe
     zweimal statt keinmal. */
  const gaitRun = useCallback(
    (kind: GaitKind) => run(model, gaitState(state, kind)),
    [run, model, state],
  )

  /* Ein Gangbild bringt seine eigenen Reglerwerte mit — sonst wäre es nur ein Name. */
  const setPreset = useCallback((key: string) => {
    setSimState((current) => {
      const preset = presetFor(current.gait, key)
      return {
        ...current,
        preset: preset.key,
        trim: { ...defaultTrim(current.gait), ...(preset.params ?? {}) },
      }
    })
  }, [])

  /*
    Eine stillgelegte Achse. Sie steht im Zustand und nicht neben ihm: alles,
    was sie ändert — Gang, Bodenkraft, Antriebe, Masse — kommt aus einem
    Durchlauf des Kerns, und der läuft über den Zustand.
  */
  const setOff = useCallback((dof: string, off: OffState | null) => {
    setSimState((current) => {
      const next = { ...current.off }
      if (off === null) delete next[dof]
      else next[dof] = off
      return { ...current, off: next }
    })
  }, [])

  /* Ein Merkmalsschalter ändert, WELCHE Modellanteile mitgerechnet werden. */
  const setFeature = useCallback((key: FeatureKey, on: boolean) => {
    setSimState((current) => ({ ...current, features: { ...current.features, [key]: on } }))
  }, [])

  /*
    Ein Modellwert. `null` nimmt die Änderung zurück; ein Wert, der dem
    Grundstand entspricht, wird beim Aufräumen ohnehin verworfen — sonst zählte
    die Oberfläche Änderungen, die keine sind.
  */
  const setEdit = useCallback(
    (key: EditKey, value: number | null) => {
      setEdits((current) => {
        const next = { ...current }
        if (value === null) delete next[key]
        else next[key] = value
        return pruneEdits(base, next)
      })
    },
    [base],
  )

  const resetEdits = useCallback(() => setEdits({}), [])

  const setRobot = useCallback((id: string) => {
    if (ROBOTS[id]) setRobotId(id)
  }, [])

  const reset = useCallback(() => {
    setSimState((current) => defaultState(current.gait))
  }, [])

  const value = useMemo(
    () => ({
      robot,
      base,
      model,
      edits,
      state,
      simulation,
      simulate,
      view,
      setView,
      overlays,
      setOverlay,
      playing,
      setPlaying,
      speed,
      setSpeed,
      phase,
      orbit,
      cameraTaken,
      setCameraTaken,
      releaseCamera,
      viewerOpen,
      setViewerOpen,
      panelOpen,
      setPanelOpen,
      bom,
      setBom,
      bomOverrides,
      setBomOverride,
      resetBomOverrides,
      focus,
      setFocus,
      manual,
      manualCheck,
      envelope,
      setEnvelope,
      setManual,
      setRobot,
      setState,
      setTrim,
      setTask,
      setGait,
      gaitRun,
      setPreset,
      setFeature,
      setOff,
      plm,
      setPlm,
      setEdit,
      resetEdits,
      reset,
    }),
    [
      robot,
      base,
      model,
      edits,
      state,
      simulation,
      simulate,
      view,
      setView,
      overlays,
      setOverlay,
      playing,
      speed,
      cameraTaken,
      releaseCamera,
      viewerOpen,
      focus,
      manual,
      manualCheck,
      envelope,
      setEnvelope,
      setManual,
      setRobot,
      setState,
      setTrim,
      setTask,
      setGait,
      gaitRun,
      setPreset,
      setFeature,
      setOff,
      plm,
      setEdit,
      resetEdits,
      reset,
    ],
  )

  return <SimulationContext.Provider value={value}>{children}</SimulationContext.Provider>
}

export function useSimulation(): SimulationContextValue {
  const value = useContext(SimulationContext)
  if (!value) throw new Error('useSimulation ausserhalb von <SimulationProvider>')
  return value
}
